// src/store/commentStore.ts
// Current time: Friday, August 29, 2025 at 1:00:00 PM UTC
// Summary: Friday, August 29, 2025, 1:00:00 PM UTC - Implemented mergeSafe for robust real-time updates and added broadcast for reactions.
/**
 * .tuMetrics {"tu1": 0.88, "tu2": 0.90, "tu3": 8, "notes": "Comment store — realtime subscription with dormancy reconnect", "tuVersion": "2.5"}
 * .help "Manages task comments with live updates and dormancy-safe reconnection."
 */

import { create } from 'zustand';
import { Comment } from '../types';
import { supabase } from '../lib/supabase';
import useUserStore from './userStore';
import useFeedStore from './feedStore';
import useFollowStore from './followStore';
import useCardStore from './cardStore';
import useSprintStore from './sprintStore';
import useRoadmapStore from './roadmapStore';
import useSharedItemStore from './sharedItemStore';
import useStreamStore from './streamStore';
import { RealtimeChannel } from '@supabase/supabase-js';
import { mapCommentFromDB, mapCommentToDB } from '../lib/supabase/queries/comment-queries';
import { followRefsFromCard, followRefsFromSprint } from '../features/feed/followMatch';

// types/supabase.ts is currently empty (generated Database types are missing), so use a
// minimal structural row type; mapCommentFromDB accepts the raw DB row.
type CommentRow = { id: string; stream_type?: string | null } & Record<string, any>;

// Comment (types/index.ts, owned by another workstream) is missing companyId even though
// the DB row has company_id and this store reads/writes it. Extend locally.
type CommentWithCompany = Comment & { companyId?: string };

// August 29, 2025 1:00 PM: Helper to safely merge DB row payloads into state (preserves reactions if omitted)
// July 2026: also preserves content/sharedItemDetails — realtime UPDATE payloads
// omit unchanged TOASTed columns (content > ~2KB), so a reaction update on a long
// comment would otherwise wipe its text (the "undefined" message bug).
function mergeSafe<
  T extends {
    id: string;
    content?: string;
    reactions?: Record<string, string[]>;
    sharedItemDetails?: unknown;
  },
>(existing: T | undefined, incoming: T): T {
  return {
    ...(existing ?? ({} as T)),
    ...incoming,
    content: incoming.content?.trim() ? incoming.content : existing?.content ?? incoming.content,
    reactions: incoming.reactions ?? existing?.reactions ?? {},
    sharedItemDetails: incoming.sharedItemDetails ?? existing?.sharedItemDetails,
  };
}

interface CommentState {
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  realtimeChannel: RealtimeChannel | null;

  initializeComments: () => Promise<void>;
  setupRealtimeSubscription: () => Promise<void>;
  /** Targeted fetch for one entity's comments — call on detail-view entry so
   *  arriving from a notification never races realtime delivery. */
  fetchCommentsForEntity: (entityType: Comment['entityType'], entityId: string) => Promise<void>;
  /** Fetch a single comment by id (heals notification previews that reference
   *  a comment not yet delivered via realtime). */
  fetchCommentById: (commentId: string) => Promise<void>;
  addComment: (data: {
    entityType: Comment['entityType'];
    entityId: string;
    content: string;
    parentCommentId?: string;
    mentions?: string[];
    streamType?: Comment['streamType'];
    recipientStreamId?: string;
    sharedItemId?: string;
    sharedItemDetails?: Comment['sharedItemDetails'];
    tags?: string[];
    // Highlight-to-comment fields
    highlightedText?: string;
    highlightPosition?: number;
    highlightEndPosition?: number;
  }) => Promise<void>;
  updateComment: (commentId: string, content: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;
  addReaction: (commentId: string, emoji: string) => Promise<void>;
  removeReaction: (commentId: string, emoji: string) => Promise<void>;
  getCommentsForEntity: (entityType: Comment['entityType'], entityId: string) => Comment[];
  cleanupRealtimeSubscription: () => void;
  reconnectRealtimeChannel: () => Promise<void>; // February 2026: Dormancy recovery
}

const useCommentStore = create<CommentState>((set, get) => ({
  comments: [],
  isLoading: false,
  error: null,
  realtimeChannel: null,

  initializeComments: async () => {
    set({ isLoading: true, error: null });
    try {
      const currentUser = useUserStore.getState().currentUser;
      if (!currentUser?.companyId) {
        console.warn('initializeComments: No current user companyId found. Skipping comment initialization.');
        set({ comments: [], isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .eq('stream_type', 'artifact_comment')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const comments = data.map(mapCommentFromDB);
      set({ comments, isLoading: false });
    } catch (error) {
      console.error('Error loading comments:', error);
      set({ error: 'Failed to load comments', isLoading: false });
    }
  },

  setupRealtimeSubscription: async () => {
    await get().initializeComments();

    get().cleanupRealtimeSubscription();

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) {
      console.warn('setupRealtimeSubscription: No current user companyId found. Skipping real-time subscription setup.');
      return;
    }

    const newChannel = supabase
      .channel('comments_channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          // July 2026: postgres_changes supports ONE filter expression only. The
          // previous "&"-joined double filter was invalid and made delivery
          // unreliable (notifications beat comments to the client). Filter by
          // company here; stream_type is checked client-side below.
          filter: `company_id=eq.${currentUser.companyId}`,
        },
        (payload) => {
          console.log('Real-time comment payload received:', payload);
          const rowNew = payload.new as CommentRow;
          const rowOld = payload.old as CommentRow;

          // Client-side guard: this store only tracks artifact comments.
          // (DELETE payloads only carry the row id, so let them through.)
          if (payload.eventType !== 'DELETE' && rowNew?.stream_type !== 'artifact_comment') {
            return;
          }

          const newComment = rowNew ? mapCommentFromDB(rowNew as Parameters<typeof mapCommentFromDB>[0]) : null;
          const oldComment = rowOld ? mapCommentFromDB(rowOld as Parameters<typeof mapCommentFromDB>[0]) : null;
          const currentUserInCallback = useUserStore.getState().currentUser;

          set((state) => {
            switch (payload.eventType) {
              case 'INSERT':
                if (currentUserInCallback && newComment && newComment.userId === currentUserInCallback.id) {
                  console.log('Real-time INSERT: Ignoring own comment (optimistically handled). ID:', newComment.id);
                  return state;
                }
                console.log('Real-time INSERT event. New comment from other user:', newComment);
                if (newComment && !state.comments.some(c => c.id === newComment.id)) {
                    return { comments: [newComment, ...state.comments] };
                }
                return state;
              case 'UPDATE':
                console.log('Real-time UPDATE event. Updated comment:', newComment);
                return {
                  comments: state.comments.map(c =>
                    c.id === (newComment?.id || '') ? mergeSafe(c, newComment!) : c // August 29, 2025 1:00 PM: Use mergeSafe for updates
                  ),
                };
              case 'DELETE':
                console.log('Real-time DELETE event. Deleted comment ID:', oldComment?.id);
                return {
                  comments: state.comments.filter(c => c.id !== (oldComment?.id || '')),
                };
              default:
                break;
            }
            return state;
          });
        }
      )
      .subscribe();

    set({ realtimeChannel: newChannel });
    console.log('CommentStore: Real-time channel subscribed successfully.');
  },

  fetchCommentsForEntity: async (entityType, entityId) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser?.companyId) return;

    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('company_id', currentUser.companyId)
        .eq('stream_type', 'artifact_comment')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const fresh = (data ?? []).map(mapCommentFromDB);
      set((state) => {
        // Replace this entity's comments with the fresh set (keep optimistic temp-
        // ids so an in-flight submit isn't dropped), leave all others untouched.
        const others = state.comments.filter(
          (c) =>
            c.entityType !== entityType ||
            c.entityId !== entityId ||
            c.id.startsWith('temp-'),
        );
        return { comments: [...fresh, ...others] };
      });
    } catch (error) {
      console.error('Error fetching comments for entity:', error);
    }
  },

  fetchCommentById: async (commentId) => {
    if (!commentId || commentId.startsWith('temp-')) return;
    if (get().comments.some((c) => c.id === commentId)) return;

    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('id', commentId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return;

      const comment = mapCommentFromDB(data);
      set((state) =>
        state.comments.some((c) => c.id === comment.id)
          ? state
          : { comments: [comment, ...state.comments] },
      );
    } catch (error) {
      console.error('Error fetching comment by id:', error);
    }
  },

  addComment: (async (data) => {
    console.log('addComment: Data received by store:', data);

    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) throw new Error('No authenticated user');
    if (!currentUser.companyId) throw new Error('Current user is not associated with a company.');

    if (data.entityType === 'direct_message' || data.entityType === 'stream_message') {
      const streamStore = useStreamStore.getState();
      await streamStore.addStreamMessage({
        streamType: data.streamType!,
        recipientStreamId: data.recipientStreamId!,
        content: data.content,
        parentCommentId: data.parentCommentId,
        mentions: data.mentions,
        sharedItemId: data.sharedItemId,
        sharedItemDetails: data.sharedItemDetails,
        tags: data.tags,
      });
      return;
    }

    let commentCompanyId: string = currentUser.companyId;
    let associatedCard = null;
    let sprintId: string | undefined = undefined;
    let roadmapId: string | undefined = undefined;
    let sharedItemDetails: Comment['sharedItemDetails'] = data.sharedItemDetails;

    if (data.sharedItemId) {
      const sharedItem = useSharedItemStore.getState().sharedItems.find(item => item.id === data.sharedItemId);
      if (sharedItem) {
        sharedItemDetails = {
          title: sharedItem.title,
          url: sharedItem.url,
          type: sharedItem.type,
          description: sharedItem.description,
          tags: sharedItem.tags,
          // Include urlMetadata if available (October 29, 2025)
          urlMetadata: (sharedItem as any).urlMetadata || undefined,
          // Include driveFileMetadata if available (November 20, 2025)
          driveFileMetadata: (sharedItem as any).driveFileMetadata || undefined,
        };
      } else {
        console.warn(`addComment: Shared item with ID ${data.sharedItemId} not found in store.`);
      }
    }

    if (data.entityType === 'task' || data.entityType === 'idea') {
      associatedCard = useCardStore.getState().cards.find(c => c.id === data.entityId);
      console.log('addComment: Associated Card:', associatedCard);
      console.log('addComment: Current User Company ID:', currentUser.companyId);
      if (associatedCard && associatedCard.companyId && associatedCard.companyId !== currentUser.companyId) {
        commentCompanyId = associatedCard.companyId;
      }
    } else if (data.entityType === 'sprint') {
      const sprint = useSprintStore.getState().sprints.find(s => s.id === data.entityId);
      if (sprint && sprint.companyId && sprint.companyId !== currentUser.companyId) {
        commentCompanyId = sprint.companyId;
      }
    } else if (data.entityType === 'roadmap') {
      const roadmap = useRoadmapStore.getState().roadmaps.find(r => r.id === data.entityId);
      if (roadmap && roadmap.companyId && roadmap.companyId !== currentUser.companyId) {
        commentCompanyId = roadmap.companyId;
      }
    }

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    const optimisticComment: CommentWithCompany = {
      id: tempId,
      userId: currentUser.id,
      entityType: data.entityType,
      entityId: data.entityId,
      content: data.content,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      parentCommentId: data.parentCommentId,
      mentions: data.mentions || [],
      reactions: {},
      companyId: commentCompanyId,
      streamType: 'artifact_comment',
      recipientStreamId: undefined,
      sharedItemId: data.sharedItemId,
      sharedItemDetails: sharedItemDetails,
      tags: data.tags || [],
      highlightedText: data.highlightedText,
      highlightPosition: data.highlightPosition,
      highlightEndPosition: data.highlightEndPosition,
    };

    set(state => ({
      comments: [optimisticComment, ...state.comments],
    }));

    try {
      const commentData = mapCommentToDB({
        userId: currentUser.id,
        entityType: data.entityType,
        entityId: data.entityId,
        content: data.content,
        parentCommentId: data.parentCommentId,
        mentions: data.mentions || [],
        reactions: {},
        companyId: commentCompanyId,
        streamType: 'artifact_comment',
        recipientStreamId: undefined,
        sharedItemId: data.sharedItemId,
        sharedItemDetails: sharedItemDetails,
        tags: data.tags || [],
        highlightedText: data.highlightedText,
        highlightPosition: data.highlightPosition,
        highlightEndPosition: data.highlightEndPosition,
      });

      console.log('addComment: Inserting comment with company_id:', commentData.company_id);
      console.log('addComment: sharedItemDetails being sent to DB:', commentData.shared_item_details);

      const { data: newComment, error } = await supabase
        .from('comments')
        .insert(commentData)
        .select()
        .single();

      if (error) throw error;

      const mappedComment = mapCommentFromDB(newComment);
      console.log('New mapped comment ID:', mappedComment.id);

      set(state => ({
        comments: state.comments.map(c =>
          c.id === tempId ? mappedComment : c
        ),
      }));

      const feedStore = useFeedStore.getState();
      const cardStore = useCardStore.getState();
      const isReply = !!data.parentCommentId;
      const eventType = isReply ? 'comment_replied' : 'comment_added';
      const cardLabel = associatedCard
        ? (associatedCard.type === 'task' ? 'Task' : 'Idea')
        : null;
      const selfTitle = associatedCard
        ? (isReply ? `Reply on ${cardLabel}` : `Comment on ${cardLabel}`)
        : 'New Message in Stream';
      const selfDescription = associatedCard
        ? (isReply
          ? `${currentUser.name} replied on "${associatedCard.title}"`
          : `${currentUser.name} commented on "${associatedCard.title}"`)
        : `${currentUser.name} sent a message to the stream`;

      console.log('Passing commentId to feedStore.addEvent:', mappedComment.id);
      // Author trail — kept so the feed card preview updates for the sender.
      feedStore.addEvent({
        type: eventType,
        title: selfTitle,
        description: selfDescription,
        userId: currentUser.id,
        initiatorId: currentUser.id,
        commentId: mappedComment.id,
        cardId: associatedCard?.id,
        sprintId: sprintId,
        roadmapId: roadmapId,
        companyId: commentCompanyId,
        streamType: 'artifact_comment',
        recipientStreamId: undefined,
        shared_item_id: data.sharedItemId,
      });

      const alreadyNotified = new Set<string>([currentUser.id]);

      if (data.parentCommentId) {
        let parentComment = get().comments.find(c => c.id === data.parentCommentId);
        if (!parentComment) {
          // Parent may not be in the local store yet (stale tab / deep reply).
          await get().fetchCommentById(data.parentCommentId);
          parentComment = get().comments.find(c => c.id === data.parentCommentId);
        }
        if (parentComment && parentComment.userId !== currentUser.id) {
          console.log('Creating reply notification for user:', parentComment.userId);
          alreadyNotified.add(parentComment.userId);
          feedStore.addEvent({
            type: 'comment_replied',
            title: 'Reply to Your Comment',
            description: `${currentUser.name} replied to your comment`,
            userId: parentComment.userId,
            initiatorId: currentUser.id,
            commentId: mappedComment.id,
            cardId: associatedCard?.id,
            sprintId: sprintId,
            roadmapId: roadmapId,
            companyId: commentCompanyId,
            streamType: 'artifact_comment',
            recipientStreamId: undefined,
            shared_item_id: data.sharedItemId,
          });
        }
      }

      // Owner + assignee hear about both top-level comments and replies on the card.
      if (data.entityType === 'task' || data.entityType === 'idea') {
        const card = associatedCard ?? cardStore.cards.find(c => c.id === data.entityId);
        const participantType = isReply ? 'comment_replied' : 'comment_added';
        const participantVerb = isReply ? 'replied on' : 'commented on';

        if (card?.createdById && !alreadyNotified.has(card.createdById)) {
          console.log('Creating comment notification for card owner:', card.createdById);
          alreadyNotified.add(card.createdById);
          feedStore.addEvent({
            type: participantType,
            title: `${isReply ? 'Reply' : 'Comment'} on Your ${card.type === 'task' ? 'Task' : 'Idea'}`,
            description: `${currentUser.name} ${participantVerb} "${card.title}"`,
            userId: card.createdById,
            initiatorId: currentUser.id,
            commentId: mappedComment.id,
            cardId: card.id,
            sprintId: sprintId,
            roadmapId: roadmapId,
            companyId: commentCompanyId,
            streamType: 'artifact_comment',
            recipientStreamId: undefined,
            shared_item_id: data.sharedItemId,
          });
        }

        if (card?.assigneeId && !alreadyNotified.has(card.assigneeId)) {
          console.log('Creating comment notification for card assignee:', card.assigneeId);
          alreadyNotified.add(card.assigneeId);
          feedStore.addEvent({
            type: participantType,
            title: `${isReply ? 'Reply' : 'Comment'} on Assigned ${card.type === 'task' ? 'Task' : 'Idea'}`,
            description: `${currentUser.name} ${participantVerb} "${card.title}"`,
            userId: card.assigneeId,
            initiatorId: currentUser.id,
            commentId: mappedComment.id,
            cardId: card.id,
            sprintId: sprintId,
            roadmapId: roadmapId,
            companyId: commentCompanyId,
            streamType: 'artifact_comment',
            recipientStreamId: undefined,
            shared_item_id: data.sharedItemId,
          });
        }
      }

      if (data.mentions?.length) {
        for (const mentionedUserId of data.mentions) {
          if (mentionedUserId !== currentUser.id) {
            feedStore.addEvent({
              type: 'comment_mentioned',
              title: 'Mentioned in Comment',
              description: `${currentUser.name} mentioned you in a comment`,
              userId: mentionedUserId,
              initiatorId: currentUser.id,
              commentId: mappedComment.id,
              cardId: associatedCard?.id,
              sprintId: sprintId,
              roadmapId: roadmapId,
              companyId: commentCompanyId,
              streamType: 'artifact_comment',
              recipientStreamId: undefined, // feedStore only truthiness-checks this; null/undefined equivalent
              shared_item_id: data.sharedItemId,
            });
          }
        }
      }

      const sprintForFollow = data.entityType === 'sprint'
        ? useSprintStore.getState().sprints.find((s) => s.id === data.entityId)
        : undefined;
      const parentAuthorId = data.parentCommentId
        ? get().comments.find((c) => c.id === data.parentCommentId)?.userId
        : undefined;
      try {
        await useFollowStore.getState().notifyFollowers(
          [
            { entityType: data.entityType, entityId: data.entityId },
            ...(associatedCard ? followRefsFromCard(associatedCard) : []),
            ...(sprintForFollow ? followRefsFromSprint(sprintForFollow) : []),
          ],
          [
            currentUser.id,
            associatedCard?.createdById,
            associatedCard?.assigneeId,
            parentAuthorId,
            ...(data.mentions ?? []),
          ],
          {
            type: data.parentCommentId ? 'comment_replied' : 'comment_added',
            title: 'Comment on something you follow',
            description: associatedCard
              ? `${currentUser.name} commented on "${associatedCard.title}"`
              : `${currentUser.name} commented`,
            initiatorId: currentUser.id,
            commentId: mappedComment.id,
            cardId: associatedCard?.id,
            sprintId: sprintId,
            roadmapId: roadmapId,
            projectId: associatedCard?.projectId ?? (data.entityType === 'project' ? data.entityId : undefined),
            requirementId: data.entityType === 'requirement' ? data.entityId : undefined,
            companyId: commentCompanyId,
            streamType: 'artifact_comment',
            shared_item_id: data.sharedItemId,
          },
        );
      } catch (followErr) {
        console.warn('Follower notify on comment failed (non-critical):', followErr);
      }
      
      // Return the new comment ID for highlight mark application
      return mappedComment.id;
    } catch (error) {
      console.error('Error adding comment:', error);
      set(state => ({
        comments: state.comments.filter(c => c.id !== tempId),
      }));
      return null;
    }
  }) as CommentState['addComment'],

  updateComment: async (commentId, content) => {
    try {
      const { error } = await supabase
        .from('comments')
        .update({ content })
        .eq('id', commentId)
        .select()
        .single();

      if (error) throw error;
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  },

  deleteComment: async (commentId) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;
    }
    catch (error) {
      console.error('Error deleting comment:', error);
    }
  },

  addReaction: async (commentId, emoji) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    try {
      const comment = get().comments.find(c => c.id === commentId);
      if (!comment) return;

      const reactions = { ...comment.reactions };
      if (!reactions[emoji]) {
        reactions[emoji] = [];
      }
      if (!reactions[emoji].includes(currentUser.id)) {
        reactions[emoji].push(currentUser.id);
      }

      set(state => ({
        comments: state.comments.map(c =>
          c.id === commentId ? { ...c, reactions } : c
        ),
      }));

      const { error } = await supabase
        .from('comments')
        .update({ reactions })
        .eq('id', commentId)
        .select();

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        set(state => ({
          comments: state.comments.map(c =>
            c.id === commentId ? comment : c
          ),
        }));
        throw error;
      }

      // August 29, 2025 1:00 PM: Send broadcast for reactions
      const { realtimeChannel } = get();
      if (realtimeChannel) {
        realtimeChannel.send({
          type: 'broadcast',
          event: 'comment_reactions', // Custom event name for comment reactions
          payload: { commentId, reactions },
        });
      }

    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  },

  removeReaction: async (commentId, emoji) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return;

    try {
      const comment = get().comments.find(c => c.id === commentId);
      if (!comment) return;

      const reactions = { ...comment.reactions };
      if (reactions[emoji]) {
        reactions[emoji] = reactions[emoji].filter(id => id !== currentUser.id);
        if (reactions[emoji].length === 0) {
          delete reactions[emoji];
        }
      }

      set(state => ({
        comments: state.comments.map(c =>
          c.id === commentId ? { ...c, reactions } : c
        ),
      }));

      const { error } = await supabase
        .from('comments')
        .update({ reactions })
        .eq('id', commentId)
        .select();

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        set(state => ({
          comments: state.comments.map(c =>
            c.id === commentId ? comment : c
          ),
        }));
        throw error;
      }

      // August 29, 2025 1:00 PM: Send broadcast for reactions
      const { realtimeChannel } = get();
      if (realtimeChannel) {
        realtimeChannel.send({
          type: 'broadcast',
          event: 'comment_reactions', // Custom event name for comment reactions
          payload: { commentId, reactions },
        });
      }

    } catch (error) {
      console.error('Error removing reaction:', error);
    }
  },

  getCommentsForEntity: (requestedEntityType, requestedEntityId) => {
    const currentUser = useUserStore.getState().currentUser;
    if (!currentUser) return [];

    return get().comments.filter(
      (comment: CommentWithCompany) => {
        if (comment.companyId !== currentUser.companyId) {
          return false;
        }

        if (comment.streamType !== 'artifact_comment') {
          return false;
        }

        return comment.entityType === requestedEntityType && comment.entityId === requestedEntityId;
      }
    );
  },

  cleanupRealtimeSubscription: () => {
    const { realtimeChannel } = get();
    if (realtimeChannel) {
      console.log('CommentStore: Unsubscribing from real-time channel.');
      supabase.removeChannel(realtimeChannel);
      set({ realtimeChannel: null });
    }
  },

  // February 2026: Dormancy recovery — tear down + re-subscribe + re-fetch comments
  reconnectRealtimeChannel: async () => {
    console.log('🔄 [CommentStore] Reconnecting realtime channel after dormancy...');
    get().cleanupRealtimeSubscription();
    await get().setupRealtimeSubscription();
    console.log('✅ [CommentStore] Realtime channel reconnected.');
  },
}));

export default useCommentStore;
